import React, { useState, useEffect } from 'react'
import { Eye, EyeOff } from 'lucide-react'
import {
  validateAndCleanInput,
  sanitizeUrlInput,
  validateEmail,
  validatePassword,
  validateName,
  validateUrl,
  validateKey,
  validateText,
  validateDate,
  validateTime,
  MAX_LENGTHS,
} from '../../utils/security'
import InfoLabel from '../InfoLabel/InfoLabel'
import styles from './SecureInput.module.css'

// Dangerous script patterns to detect and block (for name field sanitization)
const DANGEROUS_PATTERNS = [
  /<script\b[^<]*(?:(?!<\/script>)<[^<]*)*<\/script>/gi,
  /javascript:/gi,
  /on\w+\s*=/gi,
  /<iframe/gi,
  /<object/gi,
  /<embed/gi,
  /<link/gi,
  /<meta/gi,
  /<style/gi,
  /expression\s*\(/gi,
  /vbscript:/gi,
  /data:text\/html/gi,
  /eval\s*\(/gi,
  /Function\s*\(/gi,
  /setTimeout\s*\(/gi,
  /setInterval\s*\(/gi,
  /document\.(cookie|write|writeln)/gi,
  /window\.(location|open|eval)/gi,
  /\.innerHTML\s*=/gi,
  /\.outerHTML\s*=/gi,
]

export type FieldType = 'email' | 'password' | 'confirmPassword' | 'name' | 'url' | 'key' | 'text' | 'description' | 'date' | 'time' | 'number'

interface SecureInputProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  // Value and change handler
  value: string
  onChange: (value: string) => void

  // Field configuration
  fieldType: FieldType
  label?: string
  placeholder?: string
  icon?: React.ReactNode
  description?: string
  preserveWhitespace?: boolean

  // Validation
  required?: boolean
  fieldName?: string // Used in error messages (e.g., "First name", "Last name")
  matchValue?: string // For confirmPassword - value to match against

  // Standard input props
  disabled?: boolean
  className?: string
  id?: string
  name?: string
  autoComplete?: string

  // Error handling
  externalError?: string // External error to display (overrides internal validation)
  onValidationChange?: (isValid: boolean) => void // Callback when validation state changes

  // Password specific
  preventPaste?: boolean // For confirmPassword fields
  skipPasswordValidation?: boolean // Skip password strength validation (for login scenarios)
}

const SecureInput = React.forwardRef<HTMLInputElement, SecureInputProps>(
  (
    {
      value,
      onChange,
      fieldType,
      label,
      placeholder,
      icon,
      description,
      preserveWhitespace = false,
      required = false,
      fieldName,
      matchValue,
      disabled = false,
      className = '',
      id,
      name,
      autoComplete,
      externalError,
      onValidationChange,
      preventPaste = false,
      skipPasswordValidation = false,
      ...inputProps // Capture all other input props
    },
    ref
  ) => {
    const [internalError, setInternalError] = useState<string>('')
    const [touched, setTouched] = useState(false)
    const [showPassword, setShowPassword] = useState(false)

    // Determine if this is a password field
    const isPasswordField = fieldType === 'password' || fieldType === 'confirmPassword'

    // Get maxLength based on fieldType
    const getMaxLength = (): number => {
      switch (fieldType) {
        case 'email':
          return MAX_LENGTHS.email
        case 'password':
        case 'confirmPassword':
          return MAX_LENGTHS.password
        case 'name':
          return MAX_LENGTHS.name
        case 'url':
          return MAX_LENGTHS.url
        case 'key':
          return MAX_LENGTHS.key
        case 'description':
          return MAX_LENGTHS.description
        case 'date':
          return 10 // YYYY-MM-DD format
        case 'time':
          return 5 // HH:MM format
        case 'text':
        default:
          return MAX_LENGTHS.text
      }
    }

    const maxLength = getMaxLength()

    // Validate field
    const validateField = (val: string): string => {
      if (!required && !val.trim()) {
        return '' // Empty is valid if not required
      }

      let validationResult: { valid: boolean; error?: string }

      switch (fieldType) {
        case 'email':
          validationResult = validateEmail(val)
          break
        case 'password':
          if (skipPasswordValidation) {
            // For login, only check if password is not empty (if required)
            if (required && !val.trim()) {
              validationResult = { valid: false, error: 'Password is required' }
            } else {
              validationResult = { valid: true }
            }
          } else {
            validationResult = validatePassword(val)
          }
          break
        case 'confirmPassword':
          if (!val) {
            validationResult = { valid: false, error: 'Please confirm your password' }
          } else if (matchValue && val !== matchValue) {
            validationResult = { valid: false, error: 'Passwords do not match' }
          } else {
            validationResult = validatePassword(val)
          }
          break
        case 'name':
          validationResult = validateName(val, fieldName || 'Name')
          break
        case 'url':
          validationResult = validateUrl(val)
          break
        case 'key':
          validationResult = validateKey(val)
          break
        case 'date':
          validationResult = validateDate(val)
          break
        case 'time':
          validationResult = validateTime(val)
          break
        case 'description':
        case 'text':
        default:
          validationResult = validateText(val, maxLength, fieldName || 'Text')
          break
      }

      return validationResult.valid ? '' : (validationResult.error || 'Invalid input')
    }

    // Handle change
    const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
      let newValue = e.target.value

      // Don't apply validateAndCleanInput to name fields to preserve spaces
      if (!isPasswordField && fieldType !== 'date' && fieldType !== 'time' && fieldType !== 'url' && fieldType !== 'name') {
        // Never trim during typing - only strip dangerous content; trimming on blur/submit is enough
        newValue = validateAndCleanInput(newValue, false)
      } else if (fieldType === 'url') {
        // Use URL-safe sanitization that doesn't HTML-escape
        newValue = sanitizeUrlInput(newValue)
      } else if (fieldType === 'name') {
        // For name fields, only remove null bytes and dangerous patterns, but preserve spaces
        if (newValue.includes('\0')) {
          newValue = newValue.replace(/\0/g, '')
        }
        // Remove dangerous patterns but preserve spaces
        for (const pattern of DANGEROUS_PATTERNS) {
          if (pattern.test(newValue)) {
            newValue = newValue.replace(pattern, '')
          }
        }
      }

      // Enforce maxLength
      if (newValue.length > maxLength) {
        newValue = newValue.slice(0, maxLength)
      }

      onChange(newValue)

      // Clear error when user starts typing
      if (internalError) {
        setInternalError('')
      }
    }

    // Handle blur
    const handleBlur = () => {
      setTouched(true)
      const error = validateField(value)
      setInternalError(error)
    }

    // Handle paste prevention (for confirmPassword)
    const handlePaste = (e: React.ClipboardEvent<HTMLInputElement>) => {
      if (preventPaste) {
        e.preventDefault()
        setInternalError('Pasting is not allowed. Please type your password.')
      }
    }

    // Notify parent of validation state changes
    useEffect(() => {
      if (onValidationChange) {
        const error = validateField(value)
        onValidationChange(!error)
      }
    }, [value, matchValue])

    // Re-validate confirmPassword when matchValue changes
    useEffect(() => {
      if (fieldType === 'confirmPassword' && touched && matchValue !== undefined) {
        const error = validateField(value)
        setInternalError(error)
      }
    }, [matchValue])

    // Determine which error to show
    const errorToShow = externalError || (touched ? internalError : '')

    // Generate ID if not provided
    const inputId = id || name || `secure-input-${fieldType}`

    return (
      <div className={`${styles.fieldContainer} `}>
        {label && (
          <div className={styles.labelRow}>
            <label htmlFor={inputId} className={styles.label}>
              {icon && <span className={styles.icon}>{icon}</span>}
              {label}
            </label>
            {description && <InfoLabel content={description} />}
          </div>
        )}

        <div className={styles.inputWrapper}>
          <input
            ref={ref}
            id={inputId}
            name={name}
            type={fieldType === 'date' ? 'date' :
               fieldType === 'time' ? 'time' :
               fieldType === 'email' ? 'email' :
                isPasswordField ? 
                (showPassword ? 'text' : 'password') :
                 'text'} value={value}
            onChange={handleChange}
            onBlur={handleBlur}
            onPaste={handlePaste}
            className={`${className} ${styles.input} ${errorToShow ? styles.inputError : ''} `}
            placeholder={placeholder}
            disabled={disabled}
            required={required}
            maxLength={maxLength}
            autoComplete={autoComplete}
            aria-invalid={!!errorToShow}
            aria-describedby={errorToShow ? `${inputId}-error` : undefined}
            {...inputProps} // Pass through all other input attributes
          />

          {isPasswordField && (
            <button
              type="button"
              onClick={() => setShowPassword(!showPassword)}
              className={styles.passwordToggle}
              disabled={disabled}
              aria-label={showPassword ? 'Hide password' : 'Show password'}
              tabIndex={-1}
            >
              {showPassword ? <EyeOff size={18} /> : <Eye size={18} />}
            </button>
          )}
        </div>

        <span 
          id={`${inputId}-error`} 
          className={`${styles.errorMessage} ${errorToShow ? styles.errorVisible : styles.errorHidden}`} 
          role="alert"
          aria-live="polite"
        >
          {errorToShow || '\u00A0'}
        </span>
      </div>
    )
  }
)

SecureInput.displayName = 'SecureInput'

export default SecureInput
